import { readLatestChosenArtUrl } from "./finalizeOrderWithGeneratedArt";

/** Stable key for matching cart lines that include generated art. */
export function normalizeCartArtKey(art: unknown): string {
  return typeof art === "string" ? art.trim() : "";
}

type SessionArtSource = {
  snapshot?: unknown;
  processedImageUrl?: unknown;
  cartItems?: Array<{ finalCanvasImage?: unknown }>;
};

/** Resolve the user's chosen art URL across route state, Redux, storage, and cart. */
export function resolveSessionArtUrl(source: SessionArtSource = {}): string | null {
  const fromSnapshot = normalizeCartArtKey(source.snapshot);
  if (fromSnapshot) return fromSnapshot;

  const fromRedux = normalizeCartArtKey(source.processedImageUrl);
  if (fromRedux) return fromRedux;

  const fromStorage = readLatestChosenArtUrl();
  if (fromStorage) return fromStorage;

  for (const item of source.cartItems || []) {
    const fromCart = normalizeCartArtKey(item.finalCanvasImage);
    if (fromCart) return fromCart;
  }

  return null;
}

/** Art URL sent on each order line (no shared session fallback). */
export function resolveCartItemArtUrl(finalCanvasImage: unknown): string | undefined {
  const key = normalizeCartArtKey(finalCanvasImage);
  return key || undefined;
}

function stickerCartItemHaystack(item: {
  name?: unknown;
  sku_code?: unknown;
  sku?: unknown;
} | null | undefined): { name: string; sku: string; haystack: string } {
  const name =
    typeof item?.name === "string"
      ? item.name
      : item?.name && typeof item.name === "object"
        ? String((item.name as { name?: unknown }).name || "")
        : String(item?.name || "");
  const sku = String(item?.sku_code || item?.sku || "")
    .trim()
    .toLowerCase();
  const haystack = `${name} ${sku}`.toLowerCase();
  return { name, sku, haystack };
}

/** Sticker Sheet must use merch/generate mockup URL on orders (not raw session art). */
export function isStickerSheetCartItem(item: {
  name?: unknown;
  sku_code?: unknown;
  sku?: unknown;
} | null | undefined): boolean {
  const { sku, haystack } = stickerCartItemHaystack(item);
  return haystack.includes("sticker sheet") || sku === "sts" || sku.startsWith("sts");
}

/**
 * Multi-sticker packs ("2 Stickers", "3 Stickers", … "7 Stickers", etc.).
 * Orders send `image_url_single_sticker` from merch/generate (not the N-pack collage).
 */
export function isMultiStickerPackCartItem(item: {
  name?: unknown;
  sku_code?: unknown;
  sku?: unknown;
} | null | undefined): boolean {
  const { sku, haystack } = stickerCartItemHaystack(item);
  if (isStickerSheetCartItem(item)) return false;
  if (/\b\d+\s*stickers?\b/.test(haystack)) return true;
  if (/^stickers?-\d+$/.test(sku)) return true;
  if (/\bstickers\b/.test(haystack) && /\d+/.test(haystack)) return true;
  return false;
}

/** Single die-cut sticker (not sheet, not N-pack). */
export function isSingleStickerCartItem(item: {
  name?: unknown;
  sku_code?: unknown;
  sku?: unknown;
} | null | undefined): boolean {
  if (isStickerSheetCartItem(item) || isMultiStickerPackCartItem(item)) return false;
  const { sku, haystack } = stickerCartItemHaystack(item);
  if (/\bsingle\s*sticker\b/.test(haystack)) return true;
  // Shared sku ST with Single Sticker — only when name is not a multi pack.
  if (sku === "st" || sku.startsWith("st-")) {
    return !/\bstickers\b/.test(haystack);
  }
  return /\bsticker\b/.test(haystack) && !/\bstickers\b/.test(haystack);
}

/**
 * Products that use merch/generate mockup on orders:
 * single sticker + sticker sheet only (N-packs use image_url_single_sticker via resolveOrderLineImageUrl).
 */
export function isPaddedStickerCartItem(item: {
  name?: unknown;
  sku_code?: unknown;
  sku?: unknown;
} | null | undefined): boolean {
  if (isMultiStickerPackCartItem(item)) return false;
  if (isStickerSheetCartItem(item)) return true;
  return isSingleStickerCartItem(item);
}

type StickerMockupSource = {
  name?: unknown;
  sku_code?: unknown;
  sku?: unknown;
  picture?: unknown;
  imgURL?: unknown;
  image_url?: unknown;
  imageUrl?: unknown;
};

/** Catalog Single Sticker generate mockup URL (same image Single Sticker sends on order). */
export function resolveSingleStickerMockupUrl(
  products?: StickerMockupSource[] | null
): string | undefined {
  if (!Array.isArray(products) || products.length === 0) return undefined;
  const single = products.find((product) => isSingleStickerCartItem(product));
  if (!single) return undefined;
  return (
    normalizeCartArtKey(single.picture) ||
    normalizeCartArtKey(single.imgURL) ||
    normalizeCartArtKey(single.image_url) ||
    normalizeCartArtKey(single.imageUrl) ||
    undefined
  );
}

function resolveItemMockupUrl(item: {
  imageUrl?: unknown;
  imgURL?: unknown;
  image_url?: unknown;
  picture?: unknown;
} | null | undefined): string {
  return (
    normalizeCartArtKey(item?.imageUrl) ||
    normalizeCartArtKey(item?.imgURL) ||
    normalizeCartArtKey(item?.image_url) ||
    normalizeCartArtKey(item?.picture) ||
    ""
  );
}

/**
 * Order line image:
 * - Single sticker / sticker sheet → merch/generate API mockup only
 * - 2/3/4/5/6/7… Stickers → item.image_url_single_sticker (not stickers-N collage)
 * - Tote / tee / hoodie / etc. → session final art only (never generate mockup)
 */
export function resolveOrderLineImageUrl(
  item: {
    name?: unknown;
    sku_code?: unknown;
    sku?: unknown;
    imageUrl?: unknown;
    imgURL?: unknown;
    image_url?: unknown;
    image_url_single_sticker?: unknown;
    picture?: unknown;
    finalCanvasImage?: unknown;
  } | null | undefined,
  artFallback?: unknown,
  options?: { singleStickerMockupUrl?: unknown }
): string | undefined {
  const singleStickerMockup = normalizeCartArtKey(options?.singleStickerMockupUrl);
  const itemSingleSticker = normalizeCartArtKey(item?.image_url_single_sticker);
  const itemMockup = resolveItemMockupUrl(item);

  // N-packs: API single-sticker URL first, then legacy Single Sticker mockup fallback.
  if (isMultiStickerPackCartItem(item)) {
    return itemSingleSticker || singleStickerMockup || itemMockup || undefined;
  }

  // Single sticker + sticker sheet → generated merch API response only.
  if (isPaddedStickerCartItem(item)) {
    return itemMockup || undefined;
  }

  // Tote / apparel / other merch → session final art only.
  // Never send merch/generate mockup URLs (those live on imageUrl/picture for UI).
  const sessionArt = resolveCartItemArtUrl(artFallback);
  if (sessionArt) return sessionArt;

  const fromFinal = resolveCartItemArtUrl(item?.finalCanvasImage);
  if (fromFinal && fromFinal !== itemMockup) return fromFinal;

  return undefined;
}

/** Cart line types that carry size / color / art and need rich matching. */
export function isMerchCartType(type?: string | null): boolean {
  const normalized = String(type || "").trim().toLowerCase();
  return (
    normalized === "offsite-printing" ||
    normalized === "merchandise" ||
    normalized === "instore-merchandise" ||
    normalized === "product" ||
    normalized === "pack"
  );
}

/**
 * Cart/order type for a product or pack.
 * API products use `instore-merchandise`; we send that as `product`.
 */
export function resolveCartItemType(
  sourceType?: string | null,
  fallback = "product"
): string {
  const normalized = String(sourceType || "").trim().toLowerCase();
  if (!normalized) return fallback;
  if (normalized === "instore-merchandise") return "product";
  return String(sourceType || "").trim() || fallback;
}

function normalizeCartVariantKey(value?: string | null): string {
  return String(value || "").trim().toLowerCase();
}

function isProductMerchType(type?: string | null): boolean {
  const normalized = resolveCartItemType(type).toLowerCase();
  return (
    normalized === "product" ||
    normalized === "merchandise" ||
    normalized === "instore-merchandise"
  );
}

export function merchCartLineMatches(
  item: {
    productId?: string | number;
    id?: string | number;
    type?: string;
    size?: string;
    variety?: string;
    color?: string;
    finalCanvasImage?: unknown;
  },
  productId: string | number,
  opts: {
    size?: string;
    variety?: string;
    color?: string;
    finalCanvasImage?: unknown;
    type?: string;
  }
): boolean {
  const itemType = resolveCartItemType(item.type);
  const optsType = opts.type ? resolveCartItemType(opts.type) : "";
  const typeMatches = opts.type
    ? itemType === optsType
    : isMerchCartType(item.type);

  const sameProduct =
    String(item.productId || item.id) === String(productId) && typeMatches;
  if (!sameProduct) return false;

  const sameSize = normalizeCartVariantKey(item.size) === normalizeCartVariantKey(opts.size);
  const sameColor =
    normalizeCartVariantKey(item.color || item.variety) ===
    normalizeCartVariantKey(opts.color || opts.variety);

  // Product detail lines: same size + color updates the existing cart item.
  if (isProductMerchType(item.type) || isProductMerchType(opts.type)) {
    return sameSize && sameColor;
  }

  return (
    sameSize &&
    sameColor &&
    normalizeCartArtKey(item.finalCanvasImage) === normalizeCartArtKey(opts.finalCanvasImage)
  );
}

export function offsiteCartItemMatches(
  item: {
    id?: string | number;
    productId?: string | number;
    type?: string;
    size?: string;
    variety?: string;
    color?: string;
    finalCanvasImage?: unknown;
  },
  payload: {
    id: string | number;
    productId?: string | number;
    type?: string;
    size?: string;
    variety?: string;
    color?: string;
    finalCanvasImage?: unknown;
  }
): boolean {
  const expectedType = resolveCartItemType(payload.type);
  const itemType = resolveCartItemType(item.type);
  const itemProductId = item.productId ?? item.id;
  const payloadProductId = payload.productId ?? payload.id;
  const sameProduct =
    String(itemProductId) === String(payloadProductId) && itemType === expectedType;
  if (!sameProduct) return false;

  const sameSize = normalizeCartVariantKey(item.size) === normalizeCartVariantKey(payload.size);
  const sameColor =
    normalizeCartVariantKey(item.color || item.variety) ===
    normalizeCartVariantKey(payload.color || payload.variety);

  // Merch product detail: merge when size + color match; otherwise add a new line.
  if (isProductMerchType(item.type) || isProductMerchType(payload.type)) {
    return sameSize && sameColor;
  }

  return (
    sameSize &&
    sameColor &&
    normalizeCartArtKey(item.finalCanvasImage) === normalizeCartArtKey(payload.finalCanvasImage)
  );
}
